// Dedicated axios instance for the auth/users/credits API surface
// (login/register/forgot-password/verify-email/users-me/org-credits).
// Kept separate from the app's main `./client` instance — this is the same
// separation that existed inside @asabdev/asab-ui before this file moved
// here (WS-04); the two clients have historically had independent 401/refresh
// handling and there was no requirement in this workstream to unify them.
import axios, {
  type AxiosInstance,
  type AxiosRequestConfig,
  type InternalAxiosRequestConfig,
} from 'axios';
import { API_CONFIG, AUTH_TOKEN_KEY, API_ENDPOINTS } from './authConfig';

const REFRESH_TOKEN_KEY = 'refreshToken';

// Shared refresh state — deduplicates concurrent 401 → refresh attempts
let isRefreshing = false;
let failedQueue: Array<{
  resolve: (value: unknown) => void;
  reject: (reason?: unknown) => void;
  config: InternalAxiosRequestConfig;
}> = [];

// Create default axios instance
const createApiClient = (config?: AxiosRequestConfig): AxiosInstance => {
  const client = axios.create({
    baseURL: config?.baseURL || API_CONFIG.BASE_URL,
    timeout: config?.timeout || API_CONFIG.TIMEOUT,
    ...config,
  });

  // Request interceptor to add auth token
  client.interceptors.request.use(
    config => {
      const token = localStorage.getItem(AUTH_TOKEN_KEY);
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    },
    error => {
      return Promise.reject(error);
    }
  );

  // Response interceptor: 401 → refresh token → retry
  client.interceptors.response.use(
    response => response,
    async error => {
      const originalConfig = error.config as InternalAxiosRequestConfig & { _retry?: boolean };

      // Only handle 401, skip auth endpoints and already-retried requests
      if (
        error.response?.status !== 401 ||
        !originalConfig ||
        originalConfig._retry ||
        originalConfig.url?.includes('/v1/auth/')
      ) {
        return Promise.reject(error);
      }

      if (isRefreshing) {
        // Another request is already refreshing — queue this one
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject, config: originalConfig });
        });
      }

      originalConfig._retry = true;
      isRefreshing = true;

      try {
        const refreshToken = localStorage.getItem(REFRESH_TOKEN_KEY);
        if (!refreshToken) {
          processQueue(client, false);
          return Promise.reject(error);
        }

        // Use a raw axios instance (no interceptors) to avoid infinite loop
        const baseURL = originalConfig.baseURL || API_CONFIG.BASE_URL;
        const refreshResponse = await axios.post(
          `${baseURL}${API_ENDPOINTS.AUTH.REFRESH}`,
          { refresh_token: refreshToken },
          { headers: { 'Content-Type': 'application/json' } }
        );
        const data = refreshResponse.data;

        localStorage.setItem(AUTH_TOKEN_KEY, data.access_token);
        if (data.refresh_token) {
          localStorage.setItem(REFRESH_TOKEN_KEY, data.refresh_token);
        }

        processQueue(client, true);

        // Retry the original request with new token
        originalConfig.headers.Authorization = `Bearer ${data.access_token}`;
        return client.request(originalConfig);
      } catch (refreshError) {
        processQueue(client, false);
        // Refresh failed — clear auth, user will be redirected to login
        localStorage.removeItem(AUTH_TOKEN_KEY);
        localStorage.removeItem(REFRESH_TOKEN_KEY);
        localStorage.removeItem('userData');
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }
  );

  return client;
};

function processQueue(client: AxiosInstance, success: boolean) {
  failedQueue.forEach(({ resolve, reject, config }) => {
    if (success) {
      const newToken = localStorage.getItem(AUTH_TOKEN_KEY);
      if (newToken) {
        config.headers.Authorization = `Bearer ${newToken}`;
      }
      client.request(config).then(resolve).catch(reject);
    } else {
      reject(new Error('Token refresh failed'));
    }
  });
  failedQueue = [];
}

// Default client instance
const authApiClient = createApiClient();

// Export both the default client and the factory function
export default authApiClient;
export { createApiClient };

// Types for API client configuration
export interface ApiClientConfig extends AxiosRequestConfig {
  tokenStorage?: {
    getToken: () => string | null;
    setToken: (token: string) => void;
    removeToken: () => void;
  };
}
